import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { knowledgeAPI } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Loader2, Maximize2, Minimize2, Filter, Eye,
  GitBranch, ZoomIn, ZoomOut, RotateCcw, Search
} from 'lucide-react';

// Color palette for node types
const TYPE_COLORS = {
  'CLASS': '#3B82F6',
  'FUNCTION': '#22C55E',
  'METHOD': '#10B981',
  'TABLE': '#06B6D4',
  'PACKAGE BODY': '#6366F1',
  'PACKAGE': '#818CF8',
  'STRUCT': '#8B5CF6',
  'INTERFACE': '#A855F7',
  'ENUM': '#D946EF',
  'TYPE': '#EC4899',
  'MODULE': '#F59E0B',
  'FORM': '#EF4444',
  // Document types
  'DOC_CONTRACT': '#DC2626',
  'DOC_INVOICE': '#EA580C',
  'DOC_MEMO': '#CA8A04',
  'DOC_REPORT': '#65A30D',
  'DOC_MANUAL': '#0891B2',
  'DOC_POLICY': '#7C3AED',
  'DOC_OTHER': '#78716C',
  // Entity types
  'ENTITY_PERSON': '#F97316',
  'ENTITY_COMPANY': '#0EA5E9',
  'ENTITY_SYSTEM': '#8B5CF6',
  'ENTITY_PROJECT': '#14B8A6',
  'ENTITY_CASE': '#F43F5E',
  'ENTITY_REGULATION': '#A855F7',
};

const RELATION_COLORS = {
  'CALLS': '#3B82F6',
  'IMPORTS': '#22C55E',
  'SELECT': '#06B6D4',
  'INSERT': '#F59E0B',
  'UPDATE': '#F97316',
  'DELETE': '#EF4444',
  'FK_REFERENCES': '#8B5CF6',
  'MENTIONS': '#EC4899',
  'REFERENCES': '#D946EF',
};

function getNodeColor(type) {
  return TYPE_COLORS[type] || '#71717A';
}

function getNodeSize(node, links) {
  const connections = links.filter(
    l => (l.source?.id || l.source) === node.id || (l.target?.id || l.target) === node.id
  ).length;
  return Math.max(4, Math.min(16, 4 + connections * 1.5));
}

export default function GraphVisualization({ focus = 'tables' }) {
  const graphRef = useRef();
  const containerRef = useRef();
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [filterLang, setFilterLang] = useState('');
  const [filterType, setFilterType] = useState(focus === 'tables' ? 'TABLE' : '');
  const [searchHighlight, setSearchHighlight] = useState('');
  const [dimensions, setDimensions] = useState({ width: 800, height: 450 });

  const loadGraph = useCallback(async () => {
    try {
      const res = await knowledgeAPI.graphVisualization({ focus });
      setGraphData(res.data);
    } catch {
      toast.error('Error cargando datos del grafo');
    } finally {
      setLoading(false);
    }
  }, [focus]);

  useEffect(() => { loadGraph(); }, [loadGraph]);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width || 800,
          height: expanded ? 700 : 450,
        });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [expanded]);

  // Filter data
  const filteredData = useMemo(() => {
    if (!graphData) return { nodes: [], links: [] };

    let nodes = [...graphData.nodes];
    if (filterLang) {
      nodes = nodes.filter(n => n.language === filterLang);
    }
    if (filterType) {
      nodes = nodes.filter(n => n.type === filterType);
    }

    const nodeIds = new Set(nodes.map(n => n.id));
    const links = graphData.links.filter(
      l => nodeIds.has(l.source?.id || l.source) && nodeIds.has(l.target?.id || l.target)
    );

    return { nodes, links };
  }, [graphData, filterLang, filterType]);

  // Available languages and types
  const languages = useMemo(() => {
    if (!graphData) return [];
    return [...new Set(graphData.nodes.map(n => n.language))].filter(Boolean).sort();
  }, [graphData]);

  const types = useMemo(() => {
    if (!graphData) return [];
    return [...new Set(graphData.nodes.map(n => n.type))].filter(Boolean).sort();
  }, [graphData]);

  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    if (graphRef.current) {
      graphRef.current.centerAt(node.x, node.y, 500);
      graphRef.current.zoom(3, 500);
    }
  }, []);

  const handleZoomIn = () => graphRef.current?.zoom(graphRef.current.zoom() * 1.5, 300);
  const handleZoomOut = () => graphRef.current?.zoom(graphRef.current.zoom() / 1.5, 300);
  const handleReset = () => {
    graphRef.current?.centerAt(0, 0, 500);
    graphRef.current?.zoom(1, 500);
    setSelectedNode(null);
  };

  const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
    const size = getNodeSize(node, filteredData.links);
    const color = getNodeColor(node.type);
    const isHighlighted = searchHighlight && node.id.includes(searchHighlight.toUpperCase());
    const isSelected = selectedNode?.id === node.id;

    // Draw circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
    ctx.fillStyle = isSelected ? '#FBBF24' : color;
    ctx.globalAlpha = isHighlighted ? 1 : searchHighlight ? 0.15 : 0.85;
    ctx.fill();

    if (isSelected || isHighlighted) {
      ctx.strokeStyle = isSelected ? '#F59E0B' : '#3B82F6';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    ctx.globalAlpha = 1;

    // Draw label if zoomed enough
    if (globalScale > 1.2 || isSelected || isHighlighted) {
      const fontSize = Math.max(10 / globalScale, 3);
      ctx.font = `${isSelected ? 'bold ' : ''}${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillStyle = isSelected ? '#92400E' : '#09090B';
      ctx.fillText(node.id, node.x, node.y + size + fontSize + 1);
    }
  }, [filteredData.links, searchHighlight, selectedNode]);

  const linkCanvasObject = useCallback((link, ctx) => {
    const color = RELATION_COLORS[link.relation] || '#D4D4D8';
    ctx.beginPath();
    ctx.moveTo(link.source.x, link.source.y);
    ctx.lineTo(link.target.x, link.target.y);
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.3;
    ctx.lineWidth = 0.8;
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Arrow head
    const dx = link.target.x - link.source.x;
    const dy = link.target.y - link.source.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 10) return;
    const targetSize = getNodeSize(link.target, filteredData.links);
    const ratio = (len - targetSize - 2) / len;
    const ax = link.source.x + dx * ratio;
    const ay = link.source.y + dy * ratio;
    const angle = Math.atan2(dy, dx);
    const arrowLen = 4;

    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(ax - arrowLen * Math.cos(angle - Math.PI / 6), ay - arrowLen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(ax - arrowLen * Math.cos(angle + Math.PI / 6), ay - arrowLen * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.5;
    ctx.fill();
    ctx.globalAlpha = 1;
  }, [filteredData.links]);

  if (loading) return <Skeleton className="h-[450px]" />;
  if (!graphData || graphData.nodes.length === 0) {
    return (
      <div className="h-[300px] flex flex-col items-center justify-center text-zinc-400 border border-dashed border-zinc-200 rounded-sm" data-testid="graph-viz-empty">
        <GitBranch className="w-8 h-8 mb-2 opacity-30" />
        <p className="text-xs">No hay datos para visualizar</p>
        <p className="text-[10px] mt-1">Construye el grafo primero</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="graph-visualization">
      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-zinc-100 rounded-sm p-0.5">
          <Button variant="ghost" size="sm" onClick={handleZoomIn} className="h-7 w-7 p-0 rounded-sm" title="Zoom In">
            <ZoomIn className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleZoomOut} className="h-7 w-7 p-0 rounded-sm" title="Zoom Out">
            <ZoomOut className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleReset} className="h-7 w-7 p-0 rounded-sm" title="Reset">
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="h-7 w-7 p-0 rounded-sm" title={expanded ? 'Reducir' : 'Expandir'}>
            {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </Button>
        </div>

        <div className="flex items-center gap-1.5">
          <Filter className="w-3 h-3 text-zinc-400" />
          <select
            data-testid="filter-language"
            value={filterLang}
            onChange={e => setFilterLang(e.target.value)}
            className="text-[10px] h-7 px-2 rounded-sm border border-zinc-200 bg-white"
          >
            <option value="">Todos los lenguajes</option>
            {languages.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <select
            data-testid="filter-type"
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="text-[10px] h-7 px-2 rounded-sm border border-zinc-200 bg-white"
          >
            <option value="">Todos los tipos</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-1 ml-auto">
          <Search className="w-3 h-3 text-zinc-400" />
          <Input
            data-testid="graph-viz-search"
            value={searchHighlight}
            onChange={e => setSearchHighlight(e.target.value)}
            placeholder="Resaltar..."
            className="h-7 w-32 rounded-sm text-[10px]"
          />
        </div>

        <Badge variant="outline" className="text-[9px] rounded-sm">
          {filteredData.nodes.length} nodos / {filteredData.links.length} edges
        </Badge>
      </div>

      {/* Graph Canvas */}
      <div
        ref={containerRef}
        className="border border-zinc-200 rounded-sm bg-zinc-50 overflow-hidden relative"
        style={{ height: expanded ? 700 : 450 }}
        data-testid="graph-canvas"
      >
        <ForceGraph2D
          ref={graphRef}
          graphData={filteredData}
          width={dimensions.width}
          height={dimensions.height}
          nodeCanvasObject={nodeCanvasObject}
          linkCanvasObjectMode={() => 'replace'}
          linkCanvasObject={linkCanvasObject}
          onNodeClick={handleNodeClick}
          nodeLabel={n => `${n.id} (${n.type}) — ${n.language}`}
          linkLabel={l => `${l.source?.id || l.source} —[${l.relation}]→ ${l.target?.id || l.target}`}
          cooldownTicks={100}
          d3AlphaDecay={0.02}
          d3VelocityDecay={0.3}
          enableNodeDrag={true}
          enableZoomInteraction={true}
        />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 px-1">
        {Object.entries(TYPE_COLORS).filter(([type]) =>
          filteredData.nodes.some(n => n.type === type)
        ).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-[9px] text-zinc-500">{type}</span>
          </div>
        ))}
      </div>

      {/* Selected Node Detail */}
      {selectedNode && (
        <Card className="border border-amber-200 rounded-sm bg-amber-50/30" data-testid="selected-node-detail">
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-amber-600 flex items-center gap-1.5">
              <Eye className="w-3 h-3" /> {selectedNode.id}
              <Badge className="text-[8px] rounded-sm bg-amber-200 text-amber-700 border-0">{selectedNode.type}</Badge>
              {selectedNode.language && <Badge variant="outline" className="text-[8px] rounded-sm">{selectedNode.language}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 space-y-1">
            {selectedNode.file_path && (
              <p className="text-[10px] text-zinc-500 font-mono">{selectedNode.file_path}</p>
            )}
            {selectedNode.summary && (
              <p className="text-xs text-zinc-600">{selectedNode.summary}</p>
            )}
            {selectedNode.keywords?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {selectedNode.keywords.map(k => (
                  <Badge key={k} variant="outline" className="text-[8px] rounded-sm">{k}</Badge>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-1 mt-1">
              <span className="text-[9px] text-zinc-400">Conexiones:</span>
              {filteredData.links
                .filter(l => (l.source?.id || l.source) === selectedNode.id || (l.target?.id || l.target) === selectedNode.id)
                .slice(0, 10)
                .map((l, i) => {
                  const isSource = (l.source?.id || l.source) === selectedNode.id;
                  const other = isSource ? (l.target?.id || l.target) : (l.source?.id || l.source);
                  return (
                    <Badge
                      key={i}
                      className={`text-[8px] rounded-sm border-0 cursor-pointer`}
                      style={{ backgroundColor: (RELATION_COLORS[l.relation] || '#999') + '20', color: RELATION_COLORS[l.relation] || '#999' }}
                      onClick={() => {
                        const n = filteredData.nodes.find(nd => nd.id === other);
                        if (n) handleNodeClick(n);
                      }}
                    >
                      {isSource ? '→' : '←'} {l.relation}: {other}
                    </Badge>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
